import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import api from "../lib/api";

// Prompt 28 B — super-admin connections grouped by company, with search.
export default function CompanyGroups({ connections, renderItem, testid = "company-groups", limit, moreLink }) {
  const [q, setQ] = useState("");
  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const by = new Map();
    for (const raw of connections) {
      const c = raw.id ? raw : { ...raw, id: `${raw.company_id}:${raw.erp}` };
      const hay = `${c.company_name || ""} ${c.company_email || ""}`.toLowerCase();
      if (needle && !hay.includes(needle)) continue;
      if (!by.has(c.company_id)) by.set(c.company_id, { id: c.company_id, name: c.company_name,
                                                        email: c.company_email, items: [],
                                                        internal: !!c.company_internal });
      by.get(c.company_id).items.push(c);
    }
    return [...by.values()];
  }, [connections, q]);
  const shown = limit && !q.trim() ? groups.slice(0, limit) : groups;

  return (
    <div className="space-y-6" data-testid={testid}>
      <label className="flex items-center gap-2 border border-neutral-300 px-3 py-2 max-w-md">
        <Search size={14} className="text-neutral-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by company name or email"
               className="flex-1 text-sm outline-none bg-transparent" data-testid={`${testid}-search`} />
      </label>
      {groups.length === 0 && (
        <div className="text-sm text-neutral-500" data-testid={`${testid}-empty`}>
          {q ? "No companies match your search." : "No companies have connected an accounting system yet."}
        </div>
      )}
      {shown.map((g) => (
        <section key={g.id} data-testid={`${testid}-company-${g.id}`}>
          <h3 className="font-display text-lg font-semibold">{g.name}</h3>
          {g.email && <div className="text-xs text-neutral-500">{g.email}</div>}
          <InternalToggle id={g.id} initial={g.internal} testid={`${testid}-internal-${g.id}`} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">{g.items.map(renderItem)}</div>
        </section>
      ))}
      {shown.length < groups.length && moreLink && (
        <div className="text-sm" data-testid={`${testid}-more`}>
          Showing {shown.length} of {groups.length} companies · {moreLink}
        </div>
      )}
    </div>
  );
}

// Prompt 18 K1: super admin marks demo/QA companies as internal (never billed).
function InternalToggle({ id, initial, testid }) {
  const [on, setOn] = useState(initial);
  const toggle = async () => {
    const next = !on;
    try {
      await api.put(`/admin/companies/${id}/internal`, { is_internal: next });
      setOn(next);
    } catch (e) {
      window.alert("Couldn't update the internal flag. Please try again.");
    }
  };
  return (
    <label className="inline-flex items-center gap-1 text-xs text-neutral-600 mt-1 cursor-pointer">
      <input type="checkbox" checked={on} onChange={toggle} data-testid={testid} />
      internal (not billed, excluded from stats)
    </label>
  );
}
